/**
 * Floating row of round brand coins + name (brochure style). Replaces the old
 * "Kanäle · Verbunden" cards — no box, no status text.
 */
import {
  GmailBrandIcon,
  WhatsAppBrandIcon,
} from "@/app/(dashboard)/_components/settings/email-account-provider-icons";
import { ld } from "@/lib/theme/light-dark";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { AtSign, CalendarPlus, Mail } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { H } from "../../hero-tokens";

const COIN = 46;
const LOGO = 24;
/** Fixed column width → coins evenly spaced whatever the label length. */
const COLUMN = 88;

export type ChannelLogoKey =
  | "outlook"
  | "gmail"
  | "imap"
  | "whatsapp"
  | "portal"
  | "letter"
  | "calendar";

function ChannelLogo(props: { readonly channel: ChannelLogoKey }): ReactNode {
  const box = { width: LOGO, height: LOGO } as const;
  switch (props.channel) {
    case "outlook":
      return <img src="/marketing/logos/outlook.svg" alt="" aria-hidden style={box} />;
    case "gmail":
      return <GmailBrandIcon className="h-6 w-6" />;
    case "whatsapp":
      return <WhatsAppBrandIcon className="h-6 w-6" />;
    case "portal":
      return <BrandLogoMark width={LOGO + 2} height={LOGO + 2} variant="dark" />;
    case "imap":
      return <AtSign width={LOGO - 2} height={LOGO - 2} strokeWidth={2} color="#0B0F19" />;
    case "letter":
      return <Mail width={LOGO - 2} height={LOGO - 2} strokeWidth={1.9} color="#0B0F19" />;
    case "calendar":
      return <CalendarPlus width={LOGO - 2} height={LOGO - 2} strokeWidth={1.9} color="#008070" />;
  }
}

/** White coins stay white in dark mode, like app icons on a home screen. */
export function ChannelLogoRow(props: {
  readonly items: readonly { readonly channel: ChannelLogoKey; readonly label: string }[];
  readonly style: CSSProperties;
}): ReactNode {
  return (
    <ul
      style={{
        display: "flex",
        margin: 0,
        padding: 0,
        listStyle: "none",
        pointerEvents: "none",
        ...props.style,
      }}
    >
      {props.items.map((item) => (
        <li
          key={item.channel}
          style={{
            width: COLUMN,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 7,
          }}
        >
          <span
            style={{
              width: COIN,
              height: COIN,
              borderRadius: "50%",
              background: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 0 1px rgba(11,15,25,0.06), 0 14px 26px -12px ${ld("rgba(11,15,25,0.45)", "rgba(0,0,0,0.8)")}`,
            }}
          >
            <ChannelLogo channel={item.channel} />
          </span>
          <span
            style={{
              fontFamily: H.sans,
              fontSize: 11.5,
              fontWeight: 500,
              color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.65)"),
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}
